import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AppConsts } from '@shared/AppConsts';
import { ElectricalStandardDto, ElectricalStandardsService, GetElectricalStandardsInput } from '@shared/service-proxies/electrical-standards.service';

@Component({
  selector: 'app-electrical-standards-modal',
  templateUrl: './electrical-standards-modal.component.html',
  styleUrls: ['./electrical-standards-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ElectricalStandardsModalComponent implements OnInit {

  public standards: ElectricalStandardDto[] = [];
  public searchTerm: string = '';
  public isLoading: boolean = false;
  public searchLoading: boolean = false;
  public searchTimer?: number;
  public hasError: boolean = false;
  public errorMessage: string = '';
  public pageSize = 4;
  public offset = 0;
  public totalCount = 0;
  public canLoadMore = false;
  public openingId: number | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private electricalStandardsService: ElectricalStandardsService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.loadStandards();
  }

  public onSearchTermChanged(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(() => {
      this.loadStandards(true);
    }, 800);
  }

  public async loadStandards(isSearch = false, reset = false): Promise<void> {
    if (this.searchTimer && !isSearch) {
      clearTimeout(this.searchTimer);
      this.searchTimer = undefined;
    }

    if (isSearch || reset) {
      this.offset = 0;
      this.canLoadMore = false;
    }

    if (isSearch) {
      this.searchLoading = true;
    } else {
      this.isLoading = true;
    }

    this.hasError = false;
    this.errorMessage = '';

    try {
      const result = await this.electricalStandardsService.get({
        offset: this.offset,
        pageSize: this.pageSize,
        searchParms: this.searchTerm?.trim() || ''
      } as GetElectricalStandardsInput);

      const items = result?.items ?? [];
      if (this.offset === 0) {
        this.standards = items;
      } else {
        this.standards = [...this.standards, ...items];
      }

      this.totalCount = result?.totalCount ?? 0;
      this.canLoadMore = this.standards.length < this.totalCount;
    }
    catch (error) {
      this.hasError = true;
      this.errorMessage = 'Unable to load electrical standards right now.';
      this.standards = [];
    }
    finally {
      if (isSearch) {
        this.searchLoading = false;
      } else {
        this.isLoading = false;
      }
    }
  }

  public async loadMoreStandards(): Promise<void> {
    if (this.canLoadMore) {
      this.offset += this.pageSize;
      await this.loadStandards(false);
    }
  }

  public async getStandardUrl(id: string): Promise<void> {
    const numericId = Number(id);
    this.openingId = numericId;
    try {
      const result = await this.electricalStandardsService.createSignUrl(numericId);
      window.open(result.url, '_blank');
    }
    catch (error) {
      this.hasError = true;
      this.errorMessage = 'Unable to get the signed URL for the electrical standard.';
    }
    finally {
      this.openingId = null;
    }
  }

  public getStatusLabel(statusTag?: string): string | null {
    if (statusTag === 'current') {
      return 'Current';
    }
    if (statusTag === 'superseded') {
      return 'Superseded';
    }
    return null;
  }

  public getStatusTagClass(statusTag?: string): string {
    if (statusTag === 'current') {
      return 'status-tag--current';
    }
    if (statusTag === 'superseded') {
      return 'status-tag--superseded';
    }
    return '';
  }
}
